import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { ThemeProvider } from '@emotion/react';
import { useDeviceSize } from '@vmos2/providers';

import { Image } from '../image/Image';
import { IconButton } from '../buttons';
import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  backgroundColor?: string,
  defaultBorderColor?: string,
  hasImage?: boolean,
  emptyTitle?: string,
  hideDescription?: boolean,
  name?: string,
  uuid?: string,
  id?: string,
  imageSize?: {
    height?: string,
    width?: string,
  },
  imageSrc?: ?string,
  onClick?: Function,
  isEmpty?: boolean,
  className?: string,
  children?: React.Node,
  placeholder?: string,
  isFat?: boolean,
  mode?: string,
  block?: boolean,
  disabled?: boolean,
  footerLeft?: React.Node,
  footerRight?: React.Node,
  footerBottom?: React.Node,
  dataTest?: string,
  scope?: 'list' | 'grid' | 'modifier',
  content?: React.Node,
  externalImageSrc?: string,
  label?: string,
  labelMode?: string,
  prefix?: React.Node,
  as?: string,
};

/**
 * ### Available modes
 * * `default`
 * * `MealCard`
 * * `UpsellCard`
 * * `EmptyCard`
 * */

export const ListCard = withSpacingStyles(
  ({
    as,
    backgroundColor,
    children,
    defaultBorderColor,
    emptyTitle,
    hideDescription,
    name,
    uuid,
    imageSize = {},
    imageSrc,
    isEmpty,
    hasImage,
    onClick,
    placeholder,
    className,
    isFat,
    mode,
    block,
    disabled,
    footerLeft,
    footerRight,
    footerBottom,
    scope,
    dataTest = 'list-card',
    content,
    externalImageSrc,
    label,
    labelMode,
    prefix = null,
    id,
    ...rest
  }: Props) => {
    const Tag = isEmpty ? EmptyContainer : Container;
    const { isMobile } = useDeviceSize();
    const componentTheme = useComponentTheme(theme => theme.components.ListCard, isEmpty ? 'EmptyCard' : mode);

    const themeProviderValue = useMemo(
      () => ({
        componentTheme: {
          ...componentTheme,
          Container: {
            ...componentTheme.Container,
            ...(backgroundColor && { backgroundColor }),
            ...(defaultBorderColor && { defaultBorderColor }),
          },
        },
        isFat,
      }),
      [backgroundColor, componentTheme, defaultBorderColor, isFat]
    );

    const hasPadding = componentTheme.Container.hasPadding || componentTheme.Description.visible;
    const showFooterRight = footerRight && componentTheme.Footer.RightSide.visible;
    const isRightFooterText = ['string', 'number'].includes(typeof footerRight);

    const handleClick = e => {
      if (onClick && !disabled) {
        onClick(e);
      }
    };

    return (
      <ThemeProvider theme={themeProviderValue}>
        <Tag
          as={as}
          className={className}
          onClick={handleClick}
          block={block}
          disabled={disabled}
          imageHasPadding={hasPadding}
          data-test={dataTest}
          {...rest}
        >
          {prefix && <Prefix>{prefix}</Prefix>}
          {hasImage && (
            <ImageContainer hasPadding={hasPadding} imageSize={imageSize}>
              <Image
                alt={name}
                externalSrc={externalImageSrc}
                placeholder={placeholder}
                scope={scope}
                sizes="33vw"
                src={imageSrc}
                uuid={uuid}
                aria-hidden
                data-test="list-card-image"
              />
            </ImageContainer>
          )}

          {!hideDescription && (
            <Content data-test="list-card-content">
              {emptyTitle && (
                <>
                  <EmptyTitle id={id && `${id}-empty-title`} aria-hidden>
                    {emptyTitle}
                  </EmptyTitle>
                  {!!label && (
                    <Label component={componentTheme[`Label:${labelMode}`]} id={id && `${id}-overlay-text`} aria-hidden>
                      {label}
                    </Label>
                  )}
                </>
              )}

              {!isEmpty && (
                <>
                  {name && (
                    <Title aria-hidden id={id && `${id}-list-card-title`}>
                      {name}
                    </Title>
                  )}
                  {content}
                  {!!label && (
                    <Label component={componentTheme[`Label:${labelMode}`]} id={id && `${id}-overlay-text`} aria-hidden>
                      {label}
                    </Label>
                  )}

                  {(footerLeft || showFooterRight || footerBottom) && (
                    <Footer data-test="list-card-footer">
                      {footerLeft && <LeftSide>{footerLeft}</LeftSide>}
                      {showFooterRight && (
                        <RightSide
                          id={id && `${id}-footer-right-side`}
                          {...(isRightFooterText && {
                            'aria-hidden': true,
                            'aria-label': `${footerLeft ? '& ' : ''}${footerRight}.`,
                          })}
                        >
                          {footerRight}
                        </RightSide>
                      )}
                      {footerBottom && <FooterBottom>{footerBottom}</FooterBottom>}
                    </Footer>
                  )}
                </>
              )}
            </Content>
          )}

          {isEmpty ? (
            <>
              <HiddenButton
                aria-labelledby={id && `${id}-empty-title`}
                aria-describedby={id && `${id}-overlay-text`}
                onClick={e => !disabled && handleClick(e)}
              />
              <Action>
                <IconButton
                  icon="Plus"
                  data-test="add-btn"
                  onClick={e => !disabled && handleClick(e)}
                  size={isMobile ? 'xsmall' : 'small'}
                  aria-hidden
                />
              </Action>
            </>
          ) : (
            children && <Action>{children}</Action>
          )}
        </Tag>
      </ThemeProvider>
    );
  }
);

const HiddenButton = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
`;

const ImageContainer = styled.picture`
  --height: 40px;
  --margin: 12px;
  --width: 40px;
  display: flex;
  flex: none;
  width: ${({ imageSize }) => imageSize.width || 'var(--width)'};
  height: ${({ imageSize }) => imageSize.height || 'var(--height)'};
  overflow: hidden;

  ${({ hasPadding }) =>
    hasPadding &&
    `
    align-self: flex-start;
    margin: var(--margin) 0 var(--margin) var(--margin);
  `}

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    --height: 80px;
    ${({ hasPadding }) => hasPadding && `--margin: 16px;`}
    --width: 80px;
    align-self: center;

    ${({ theme }) =>
      theme.isFat &&
      `
      --height: 136px;
      --width: 136px;
    `}
  }

  @media (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    ${({ theme }) =>
      theme.isFat &&
      `
      --height: 196px;
      --width: 196px;
    `}
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.componentTheme.Image.borderRadius};
  }
`;

const Prefix = styled('div')`
  display: flex;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 12px;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: 16px;
  }
`;

const Content = styled('div')`
  display: flex;
  flex-flow: column;
  flex: 1 1 auto;
  padding: 12px;
  min-width: 0;
  min-height: 64px;
  align-self: normal;
  justify-content: center;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    padding: 16px;
  }

  @media screen and (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    padding: ${({ theme }) => theme.isFat && '0 24px'};
  }
`;

const EmptyContainer = styled('div')`
  display: flex;
  cursor: pointer;
  position: relative;
  border: ${({ theme }) => theme.stroke.dashed};
  background: ${({ theme }) => theme.componentTheme.Container.backgroundColor || theme.color.white};
  border-radius: ${({ theme }) => theme.componentTheme.Container.borderRadius};
  ${({ theme }) => theme.isFat && 'align-items: center'};
  overflow: hidden;
  ${({ block }) => block && `width: 100%`};
  min-height: 64px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => disabled && 0.25};

  ${ImageContainer} {
    ${({ imageHasPadding }) => (imageHasPadding ? `margin: 12px 0 12px 12px` : `margin: 0 12px 0 0`)};
  }

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    ${({ theme }) => theme.isFat && 'padding: 24px'};
    flex-direction: ${({ theme }) => (theme.isFat ? 'column' : 'row')};
    justify-content: center;
    min-height: 80px;

    ${ImageContainer} {
      flex: 0 0 76px;
      ${({ imageHasPadding }) => (imageHasPadding ? `margin: 16px 0 16px 16px` : `margin: -16px 0 -16px 0`)};
    }
  }

  @media screen and (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    padding: ${({ theme }) => theme.isFat && '48px'};
  }

  ${Content} {
    align-self: center;
    min-height: auto;
  }
`;

const Container = styled('div')`
  display: flex;
  position: relative;
  background: ${({ theme }) => theme.componentTheme.Container.backgroundColor || theme.color.white};
  z-index: 0;
  border-radius: ${({ theme }) => theme.componentTheme.Container.borderRadius};
  overflow: hidden;
  flex-wrap: nowrap;
  align-items: stretch;
  ${({ block }) => block && `width: 100%`};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => disabled && 0.25};

  ${({ theme }) => {
    const {
      componentTheme: {
        Container: { boxShadow, defaultBorder, defaultBorderColor, showBottomBorder },
      },
    } = theme;
    let styles = ``;

    if (defaultBorder && defaultBorderColor !== 'transparent') {
      styles += `border: ${defaultBorder};`;

      if (defaultBorderColor) {
        styles += `border-color: ${defaultBorderColor};`;
      }
    } else {
      styles += `box-shadow: ${boxShadow};`;
    }

    if (showBottomBorder) {
      styles += `
        &:not(:last-of-type) {
          border-bottom: ${theme.stroke.light};
        }
      `;
    }

    return styles;
  }};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    min-height: ${({ theme }) => (theme.isFat ? '136px' : '80px')};
  }

  @media screen and (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    min-height: ${({ theme }) => (theme.isFat ? '196px' : '80px')};
  }
`;

const Action = styled('div')`
  display: flex;
  justify-content: ${({ theme }) => (theme.isFat ? 'center' : 'flex-end')};
  align-items: center;
  padding: ${({ theme }) => (theme.isFat ? '0 12px 12px' : '12px 12px 12px 0')};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    padding: ${({ theme }) => (theme.isFat ? '0 16px 16px' : '16px')};
  }

  ${({ theme }) =>
    theme.isFat &&
    `
    @media (${theme.breakpoint.largeScreenKiosk}) {
      padding: 0 24px;
    }
  `};
`;

const Title = styled('h5')`
  margin: 0;
  color: ${({ theme: { componentTheme } }) => componentTheme.Title.color};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme: { componentTheme } }) => componentTheme.Title.fontWeight};
  font-family: ${({ theme }) =>
    theme.componentTheme.Title.secondaryFontFamily ? theme.typography.secondaryFontFamily : 'inherit'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme: { componentTheme } }) => componentTheme.Title.fontSize};
  }

  @media screen and (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    ${({ theme }) => theme.isFat && `font-size: 24px;`};
  }
`;

const EmptyTitle = styled(Title)`
  white-space: break-spaces;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-wrap: break-word;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    align-items: ${({ theme }) => (theme.isFat ? 'center' : 'normal')};
    margin-bottom: ${({ theme }) => (theme.isFat ? '16px' : 0)};
    justify-content: ${({ theme }) => (theme.isFat ? 'center' : 'left')};
  }

  @media screen and (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    margin-bottom: ${({ theme }) => theme.isFat && '24px'};
  }
`;

const Footer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
`;

const LeftSide = styled('span')`
  margin-right: 32px;
  margin-top: 8px;
  font-weight: ${({ theme: { componentTheme } }) => componentTheme.Footer.LeftSide.fontWeight};
  color: ${({ theme: { componentTheme } }) => componentTheme.Footer.LeftSide.color};
  font-size: ${({ theme }) => theme.fontSize.sm};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme: { componentTheme } }) => componentTheme.Footer.LeftSide.fontSize};
  }
`;

const RightSide = styled('span')`
  margin-top: 8px;
  font-weight: ${({ theme: { componentTheme } }) => componentTheme.Footer.RightSide.fontWeight};
  color: ${({ theme: { componentTheme } }) => componentTheme.Footer.RightSide.color};
  font-size: ${({ theme }) => theme.fontSize.xs};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme: { componentTheme } }) => componentTheme.Footer.RightSide.fontSize};
  }
`;

const FooterBottom = styled.div`
  flex-basis: 100%;
`;

const Label = styled('div')`
  width: fit-content;
  height: fit-content;
  padding: 4px 8px;
  border-radius: ${({ component }) => component.borderRadius};
  background-color: ${({ component }) => component.backgroundColor};
  color: ${({ component }) => component.color};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: 8px;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ component }) => component.fontSize};
  }
`;
